<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['error']);
const showGallery = ref(false);
const videoPlayer = useTemplateRef('videoPlayer');

const { filteredCurrentChatAttachments } = useMessageContext();

const appendTimestamp = url => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('t', Date.now());
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const videoSrc = computed(() => {
  if (!props.attachment.dataUrl) return '';

  return appendTimestamp(props.attachment.dataUrl);
});

const resetVideo = async () => {
  await nextTick();
  videoPlayer.value?.load();
};

const onError = () => {
  emit('error');
};

watch(videoSrc, resetVideo);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer relative group"
    @click="showGallery = true"
  >
    <video
      ref="videoPlayer"
      :key="videoSrc"
      :src="videoSrc"
      class="w-full h-full object-cover"
      muted
      playsInline
      @error="onError"
    />
    <div
      class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
    >
      <div
        class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
      >
        <Icon
          icon="i-teenyicons-play-small-solid"
          class="size-7 text-n-slate-12/80 backdrop-blur"
        />
      </div>
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
